import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Bot, BotOff, ShieldAlert, ShieldQuestion } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CustomerLogResponseDataSchema } from "schema"
import z from "zod"

type CustomerLog = z.infer<typeof CustomerLogResponseDataSchema>

type Props = {
  log: CustomerLog
}

export function CustomerLogMarker({ log }: Props) {
  const { i18n, t } = useTranslation()
  const isAiAuditLog = log.kind === "AIDisabled" || log.kind === "AIResumed"
  const author = log.admin ?? log.employee
  const formattedTime = new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(log.createdAt))

  let icon
  let label
  switch (log.kind) {
    case "AIDisabled":
      icon = <BotOff size={16} className="shrink-0" />
      label = t("conversations.chat.aiDisabled")
      break
    case "AIResumed":
      icon = <Bot size={16} className="shrink-0" />
      label = t("conversations.chat.aiResumed")
      break
    case "FraudDetected":
      icon = <ShieldAlert size={16} className="shrink-0" />
      label = t("conversations.chat.fraudDetected")
      break
    case "BrandRelatedFraudReported":
      icon = <ShieldQuestion size={16} className="shrink-0" />
      label = t("conversations.chat.brandRelatedFraudReported")
      break
    case "TrollingDetected":
      icon = <ShieldQuestion size={16} className="shrink-0" />
      label = t("conversations.chat.trollingDetected")
      break
    default:
      icon = undefined
      label = undefined
  }

  if (!label) return null

  const marker = (
    <div className="flex gap-1.5 px-4 bg-background z-10 relative rounded-full">
      {icon}
      {label}
    </div>
  )

  if (!isAiAuditLog) {
    return (
      <div
        className="w-full flex items-center justify-center text-muted-foreground/70 text-xs font-medium relative"
        id={`customer-log-${log.id}`}
      >
        <div className="border border-b border-muted-foreground/20 absolute w-full" />
        {marker}
      </div>
    )
  }

  return (
    <div
      className="w-full flex items-center justify-center text-muted-foreground/70 text-xs font-medium relative"
      id={`customer-log-${log.id}`}
    >
      <div className="border border-b border-muted-foreground/20 absolute w-full" />
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex gap-1.5 px-4 bg-background z-10 relative rounded-full cursor-pointer"
            type="button"
          >
            {icon}
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent className="flex flex-col gap-1.5 text-sm">
          {log.source === "automatic" && <p>{t("conversations.chat.aiAudit.automatic")}</p>}
          {log.source === "workflow" && <p>{t("conversations.chat.aiAudit.workflow")}</p>}
          {log.source && !author && log.source !== "automatic" && log.source !== "workflow" && (
            <p>{t("conversations.chat.aiAudit.integration")}</p>
          )}
          {!log.source && !author && <p>{t("conversations.chat.aiAudit.unattributed")}</p>}
          {author && <p>{t("conversations.chat.aiAudit.changedBy", { name: author.name })}</p>}
          <time className="self-end text-xs text-muted-foreground">{formattedTime}</time>
        </PopoverContent>
      </Popover>
    </div>
  )
}
